import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { fetchSecondBrainChats } from '../services/secondBrainService';
import { SecondBrainChat } from '../types';
import { errorReporter } from '@/services/errorReportingService';

export interface UseSecondBrainHistoryReturn {
  chatHistory: SecondBrainChat[];
  activeChatId: string | null;
  activeChat: SecondBrainChat | null;
  isLoadingHistory: boolean;
  setActiveChatId: (id: string | null) => void;
  setChatHistory: React.Dispatch<React.SetStateAction<SecondBrainChat[]>>;
  refreshChatHistory: (focusId?: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  clearActiveChat: () => void;
}

export const useSecondBrainHistory = (): UseSecondBrainHistoryReturn => {
  const { userToken } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);

  const [chatHistory, setChatHistory] = useState<SecondBrainChat[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  const activeChat = chatHistory.find((chat) => chat.id === activeChatId) ?? null;

  const refreshChatHistory = useCallback(
    async (focusId?: string) => {
      if (!userToken) {
        return;
      }

      setIsLoadingHistory(true);
      try {
        const chats = await fetchSecondBrainChats({ token: userToken });
        setChatHistory(chats);
        // No auto-select: the landing (home) state must show when nothing is open
        setActiveChatId((current) => focusId ?? current ?? null);
      } catch (error) {
        errorReporter.expected('Failed to load Second Brain history', error, {
          feature: 'secondBrain',
          operation: 'loadHistory',
        });
        if (error instanceof Error && error.message === 'BACKEND_NOT_AVAILABLE') {
          showAlert('Second Brain backend API is not available yet. This feature is still in development.', 'warning');
        } else {
          showAlert('Unable to load Second Brain chats right now. Please refresh later.', 'error');
        }
        setChatHistory([]);
        setActiveChatId(null);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [showAlert, userToken],
  );

  const deleteChat = useCallback(
    async (chatId: string) => {
      if (!userToken) {
        return;
      }

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/second-brain/chats/${chatId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete chat');
        }

        setChatHistory((prev) => prev.filter((chat) => chat.id !== chatId));
        if (activeChatId === chatId) {
          setActiveChatId(null);
        }
        showAlert('Chat deleted successfully', 'success');
      } catch (error) {
        errorReporter.expected('Failed to delete Second Brain chat', error, {
          feature: 'secondBrain',
          operation: 'deleteChat',
        });
        showAlert('Failed to delete chat. Please try again.', 'error');
      }
    },
    [userToken, activeChatId, showAlert],
  );

  const clearActiveChat = useCallback(() => {
    setActiveChatId(null);
  }, []);

  useEffect(() => {
    void refreshChatHistory();
  }, [refreshChatHistory]);

  return {
    chatHistory,
    activeChatId,
    activeChat,
    isLoadingHistory,
    setActiveChatId,
    setChatHistory,
    refreshChatHistory,
    deleteChat,
    clearActiveChat,
  };
};
